import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { FiCheckCircle } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { formatMoney } from '@/lib/utils';
import {
  getExpenseRemainingCents,
  type UnpaidExpenseLike,
} from '@/lib/expense-settlement';
import type { ExpenseListResponse, UserResponse } from '@/lib/types';

export type UnpaidExpenseOption = UnpaidExpenseLike;

type UnpaidExpensePickerProps = {
  selectedExpenseId: number | null;
  onSelect: (expense: UnpaidExpenseOption | null) => void;
  maxItems?: number;
};

export function UnpaidExpensePicker({
  selectedExpenseId,
  onSelect,
  maxItems = 5,
}: UnpaidExpensePickerProps) {
  const { getToken } = useAuth();

  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  const unpaidExpensesQuery = useQuery({
    queryKey: ['expenses', 'unpaid'],
    queryFn: () =>
      apiJson<ExpenseListResponse>(getToken, '/v1/expenses?is_paid=false'),
  });

  const currentUserId = userQuery.data?.id;
  const expenses = (unpaidExpensesQuery.data?.expenses ?? [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.purchase_date).getTime() - new Date(a.purchase_date).getTime()
    )
    .slice(0, maxItems);

  if (unpaidExpensesQuery.isLoading) {
    return (
      <div className='py-4 flex justify-center'>
        <div className='animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (unpaidExpensesQuery.isError) {
    return (
      <p className='text-sm text-gray-500'>Couldn&apos;t load recent expenses.</p>
    );
  }

  if (expenses.length === 0) {
    return (
      <p className='text-sm text-gray-500'>No unpaid expenses to link right now.</p>
    );
  }

  return (
    <div className='space-y-2'>
      {expenses.map((expense) => {
        const isSelected = selectedExpenseId === expense.id;
        const remainingCents = getExpenseRemainingCents(expense, currentUserId);

        return (
          <button
            key={expense.id}
            type='button'
            onClick={() => onSelect(isSelected ? null : expense)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
              isSelected
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className='flex-1 min-w-0'>
              <p className='font-bold text-gray-900 truncate'>{expense.name}</p>
              <p className='text-sm text-gray-500'>
                {new Date(expense.purchase_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
                {' · '}
                {formatMoney(expense.amount_cents)} total
                {remainingCents > 0 ? ` · ${formatMoney(remainingCents)} owed` : ''}
              </p>
            </div>
            {isSelected ? (
              <FiCheckCircle className='w-5 h-5 text-primary shrink-0' />
            ) : (
              <div className='w-5 h-5 rounded-full border-2 border-gray-300 shrink-0' />
            )}
          </button>
        );
      })}
    </div>
  );
}
